import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { explainGoldenDiff } from "./api";
import {
  formatEvalMoney,
  getComponentRows,
  getDifferencePath,
  getDifferences,
  getDifferenceSummary,
  getGoldenLoanId,
  getHumanAmount,
  getJudgeVerdict,
  getLoanName,
  getRunLoans,
  getSourceAmount,
  getSourceAvailableAmount,
  getSuiteRunId,
} from "./goldenEval";

function describeChange(left, right) {
  const hasLeft = typeof left === "number" && Number.isFinite(left);
  const hasRight = typeof right === "number" && Number.isFinite(right);
  if (!hasLeft && !hasRight) {
    return { label: "—", tone: "empty", changed: false };
  }
  if (!hasLeft) {
    return { label: "Only on right", tone: "added", changed: true };
  }
  if (!hasRight) {
    return { label: "Only on left", tone: "removed", changed: true };
  }
  if (left === right) {
    return { label: "Same", tone: "same", changed: false };
  }

  const delta = Math.round((right - left) * 100) / 100;

  return {
    label: `${delta > 0 ? "+" : ""}${formatEvalMoney(delta)}`,
    tone: delta > 0 ? "up" : "down",
    changed: true,
  };
}

function ChangeChip({ left, right }) {
  const change = describeChange(left, right);

  return (
    <span className={`icr-change-chip icr-change-chip--${change.tone}`}>
      {change.label}
    </span>
  );
}

function MoneyPairCell({ available, qualifying }) {
  return (
    <div className="icr-money-pair-cell">
      <span>
        <em>Avail</em> {formatEvalMoney(available)}
      </span>
      <span>
        <em>Qual</em> {formatEvalMoney(qualifying)}
      </span>
    </div>
  );
}

function DualChangeCell({ leftKey, rightKey, row }) {
  return (
    <div className="icr-dual-change-cell">
      <span>
        <em>Avail</em>{" "}
        <ChangeChip left={row[`${leftKey}Available`]} right={row[`${rightKey}Available`]} />
      </span>
      <span>
        <em>Qual</em>{" "}
        <ChangeChip left={row[leftKey]} right={row[rightKey]} />
      </span>
    </div>
  );
}

function rowHasComparisonChange(row, group) {
  return (
    describeChange(row[`${group.leftKey}Available`], row[`${group.rightKey}Available`])
      .changed || describeChange(row[group.leftKey], row[group.rightKey]).changed
  );
}

function detailSummary(detail) {
  return (
    detail?.decisionReason ||
    detail?.formula ||
    (detail?.documents?.length
      ? `Used ${detail.documents.length} supporting document${
          detail.documents.length === 1 ? "" : "s"
        }`
      : null)
  );
}

function componentReason(row) {
  const addyAvail = formatEvalMoney(row.addyAvailable);
  const addyQual = formatEvalMoney(row.addy);
  const harnessAvail = formatEvalMoney(row.harnessAvailable);
  const harnessQual = formatEvalMoney(row.harness);

  if (
    row.addyAvailable === row.harnessAvailable &&
    row.addy === row.harness &&
    typeof row.addy === "number"
  ) {
    return `Addy and Harness agree on available (${addyAvail}) and qualifying (${addyQual}).`;
  }

  const addyReason = detailSummary(row.addyDetail);
  const harnessReason = detailSummary(row.harnessDetail);
  const base = `Addy available ${addyAvail}, qualifying ${addyQual}; Harness available ${harnessAvail}, qualifying ${harnessQual}.`;

  if (addyReason && harnessReason) {
    return `${base} Addy: ${addyReason}. Harness: ${harnessReason}.`;
  }
  if (addyReason) {
    return `${base} Addy: ${addyReason}.`;
  }
  if (harnessReason) {
    return `${base} Harness: ${harnessReason}.`;
  }

  return `${base} Expand calculation evidence for document and formula details.`;
}

function renderAuditValue(value) {
  if (value == null || value === "") {
    return "Not provided";
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return JSON.stringify(value);
}

function SourceAudit({ label, available, qualifying, status, detail }) {
  return (
    <div className="icr-source-audit">
      <h6>{label}</h6>
      <dl>
        <div>
          <dt>Available monthly</dt>
          <dd>{formatEvalMoney(available)}</dd>
        </div>
        <div>
          <dt>Qualifying monthly</dt>
          <dd>{formatEvalMoney(qualifying)}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>{status || "Not provided"}</dd>
        </div>
        <div>
          <dt>Formula</dt>
          <dd>{detail?.formula || "Not provided"}</dd>
        </div>
        <div>
          <dt>Decision reason</dt>
          <dd>{detail?.decisionReason || "Not provided"}</dd>
        </div>
        <div>
          <dt>Evidence</dt>
          <dd>
            {detail?.documents?.length
              ? detail.documents.map(renderAuditValue).join("; ")
              : "Not provided"}
          </dd>
        </div>
        <div>
          <dt>Source values</dt>
          <dd>
            {detail?.sourceValues?.length
              ? detail.sourceValues.map(renderAuditValue).join("; ")
              : "Not provided"}
          </dd>
        </div>
        <div>
          <dt>Guideline</dt>
          <dd>{renderAuditValue(detail?.guidelineReference)}</dd>
        </div>
      </dl>
    </div>
  );
}

function SourceSummary({ label, available, qualifying, optional, children }) {
  return (
    <div className="icr-eval-source">
      <span>
        {label} {optional && <em>optional</em>}
      </span>
      {children || (
        <div className="icr-money-pair-cell">
          <strong>
            <em>Avail</em> {formatEvalMoney(available)}
          </strong>
          <strong>
            <em>Qual</em> {formatEvalMoney(qualifying)}
          </strong>
        </div>
      )}
    </div>
  );
}

const ADDY_HARNESS_PAIR = {
  key: "addy-harness",
  label: "Addy → Harness",
  leftSource: "addy",
  rightSource: "harness",
  leftKey: "addy",
  rightKey: "harness",
  evaluationKey: "addyVsHarness",
};

const HUMAN_COMPARISON_PAIRS = [
  {
    key: "human-addy",
    label: "Human → Addy",
    leftSource: "human",
    rightSource: "addy",
    leftKey: "human",
    rightKey: "addy",
    evaluationKey: "humanVsAddy",
  },
  {
    key: "human-harness",
    label: "Human → Harness",
    leftSource: "human",
    rightSource: "harness",
    leftKey: "human",
    rightKey: "harness",
    evaluationKey: "humanVsHarness",
  },
];

function explanationKey(leftSource, rightSource, path) {
  return `${leftSource}->${rightSource}:${path}`;
}

function buildComparisonGroups(loan, hasHumanTruth) {
  const pairs = hasHumanTruth
    ? [...HUMAN_COMPARISON_PAIRS, ADDY_HARNESS_PAIR]
    : [ADDY_HARNESS_PAIR];

  return pairs.map((pair) => {
    const evaluation = loan?.evaluation?.[pair.evaluationKey];
    const evaluationDifferences = evaluation?.differences;
    const differences = Array.isArray(evaluationDifferences)
      ? evaluationDifferences
      : pair.key === ADDY_HARNESS_PAIR.key
        ? getDifferences(loan)
        : [];

    return { ...pair, evaluation, differences };
  });
}

function TrendChart({ runs, goldenLoanId }) {
  const points = runs
    .map((run, index) => {
      const loan = getRunLoans(run).find(
        (item) => getGoldenLoanId(item) === goldenLoanId,
      );
      if (!loan) {
        return null;
      }

      return {
        index,
        label: `Run ${runs.length - index}`,
        addy: getSourceAmount(loan.addy),
        harness: getSourceAmount(loan.harness),
        human: getHumanAmount(loan),
      };
    })
    .filter(Boolean);
  const values = points.flatMap((point) =>
    [point.addy, point.harness, point.human].filter(
      (value) => typeof value === "number",
    ),
  );

  if (points.length < 2 || values.length === 0) {
    return (
      <p className="icr-muted">
        Two or more suite results are needed to draw this loan’s trend.
      </p>
    );
  }

  const width = 720;
  const height = 190;
  const pad = 24;
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const range = maximum - minimum || 1;
  const xFor = (index) =>
    pad + (index / Math.max(points.length - 1, 1)) * (width - pad * 2);
  const yFor = (value) =>
    height - pad - ((value - minimum) / range) * (height - pad * 2);
  const polyline = (key) =>
    points
      .map((point, index) =>
        typeof point[key] === "number"
          ? `${xFor(index)},${yFor(point[key])}`
          : null,
      )
      .filter(Boolean)
      .join(" ");

  return (
    <div className="icr-trend-chart">
      <div className="icr-trend-legend" aria-hidden="true">
        <span className="icr-trend-legend--addy">Addy</span>
        <span className="icr-trend-legend--harness">Harness</span>
        <span className="icr-trend-legend--human">Human (when labeled)</span>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Latest ${points.length} suite results for ${getLoanName(
          getRunLoans(runs[0]).find(
            (item) => getGoldenLoanId(item) === goldenLoanId,
          ),
        )}. Addy, Harness, and optional Human monthly amounts.`}
      >
        <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} />
        <polyline
          className="icr-trend-line icr-trend-line--addy"
          points={polyline("addy")}
        />
        <polyline
          className="icr-trend-line icr-trend-line--harness"
          points={polyline("harness")}
        />
        <polyline
          className="icr-trend-line icr-trend-line--human"
          points={polyline("human")}
        />
        {points.map((point, index) => (
          <g key={`${point.label}-${index}`}>
            {["addy", "harness", "human"].map(
              (source) =>
                typeof point[source] === "number" && (
                  <circle
                    key={source}
                    className={`icr-trend-dot icr-trend-dot--${source}`}
                    cx={xFor(index)}
                    cy={yFor(point[source])}
                    r="4"
                  >
                    <title>
                      {point.label} {source}: {formatEvalMoney(point[source])}
                    </title>
                  </circle>
                ),
            )}
            <text x={xFor(index)} y={height - 6}>
              {points.length <= 6 || index % 2 === 0 ? point.label : ""}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function GoldenLoanEvalDetail({
  env,
  loan,
  suiteRun,
  recentRuns,
}) {
  const [showUnchanged, setShowUnchanged] = useState(true);
  const [explanations, setExplanations] = useState({});
  const [legacyExplanations, setLegacyExplanations] = useState([]);
  const [loadingExplanationKey, setLoadingExplanationKey] = useState("");
  const suiteRunId = getSuiteRunId(suiteRun);
  const goldenLoanId = getGoldenLoanId(loan);
  const components = useMemo(() => getComponentRows(loan), [loan]);
  const humanAmount = getHumanAmount(loan);
  const hasHumanTruth =
    humanAmount != null || loan?.evaluation?.hasHumanTruth === true;
  const comparisonGroups = useMemo(
    () => buildComparisonGroups(loan, hasHumanTruth),
    [hasHumanTruth, loan],
  );
  const reportedDifferenceCount = comparisonGroups.reduce(
    (total, group) => total + group.differences.length,
    0,
  );

  useEffect(() => {
    const cached = {};
    const legacy = [];
    comparisonGroups.forEach((group) => {
      group.differences.forEach((difference, index) => {
        if (difference?.explanation) {
          const path = getDifferencePath(difference, index);
          cached[explanationKey(group.leftSource, group.rightSource, path)] =
            difference.explanation;
        }
      });
    });
    Object.entries(loan?.explanations || {}).forEach(([storageKey, record]) => {
      if (typeof record === "string") {
        legacy.push({ key: storageKey, text: record });

        return;
      }
      const text = record?.text || record?.explanation;
      if (record?.path && text) {
        const leftSource = record.leftSource || "addy";
        const rightSource = record.rightSource || "harness";
        cached[explanationKey(leftSource, rightSource, record.path)] = text;
      } else if (text) {
        legacy.push({ key: storageKey, text });
      }
    });
    setExplanations(cached);
    setLegacyExplanations(legacy);
  }, [comparisonGroups, goldenLoanId, loan, suiteRunId]);

  if (!loan) {
    return null;
  }

  const visibleComponents = components.filter((row) => {
    if (showUnchanged) {
      return true;
    }

    return comparisonGroups.some((group) => rowHasComparisonChange(row, group));
  });
  const explain = async (group, difference, index) => {
    const path = getDifferencePath(difference, index);
    const key = explanationKey(group.leftSource, group.rightSource, path);
    if (explanations[key]) {
      return;
    }
    setLoadingExplanationKey(key);
    try {
      const result = await explainGoldenDiff(
        goldenLoanId,
        {
          suiteRunId,
          leftSource: group.leftSource,
          rightSource: group.rightSource,
          path,
        },
        env,
      );
      if (!result.success) {
        throw new Error(result.reason || "Could not explain this difference.");
      }
      const explanation = result.explanation || result.summary;
      const explanationText =
        typeof explanation === "string" ? explanation : explanation?.text;
      setExplanations((current) => ({
        ...current,
        [key]: explanationText || "No explanation returned.",
      }));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingExplanationKey("");
    }
  };

  return (
    <section className="icr-loan-eval" aria-labelledby="loan-eval-title">
      <div className="icr-eval-section__header">
        <div>
          <span className="icr-eval-eyebrow">Loan eval detail</span>
          <h3 id="loan-eval-title">{getLoanName(loan)}</h3>
          <p className="icr-mono">{goldenLoanId}</p>
        </div>
        <span className="icr-judge-badge">
          Judge verdict: {getJudgeVerdict(loan)}
        </span>
      </div>

      <div className="icr-eval-sources">
        <SourceSummary
          label="Human"
          available={getSourceAvailableAmount(loan.human || loan.humanLabel)}
          qualifying={humanAmount}
          optional
        />
        <SourceSummary
          label="Addy"
          available={getSourceAvailableAmount(loan.addy)}
          qualifying={getSourceAmount(loan.addy)}
        />
        <SourceSummary
          label="Harness"
          available={getSourceAvailableAmount(loan.harness)}
          qualifying={getSourceAmount(loan.harness)}
        />
        <SourceSummary label="Judge">
          <strong className="icr-eval-source__verdict">
            {getJudgeVerdict(loan)}
          </strong>
          <small>Reviewer of Harness; no independent amount</small>
        </SourceSummary>
      </div>

      {!hasHumanTruth && (
        <div className="icr-no-human-truth">
          <strong>No human truth provided</strong>
          <span>
            Agreement still compares Addy with Harness. Human accuracy is
            intentionally excluded.
          </span>
        </div>
      )}

      <div className="icr-compare-toolbar">
        <div className="icr-compare-legend">
          <span className="icr-change-chip icr-change-chip--same">Same</span>
          <span className="icr-change-chip icr-change-chip--up">+$…</span>
          <span className="icr-change-chip icr-change-chip--down">−$…</span>
          <span className="icr-change-chip icr-change-chip--added">
            Only on right
          </span>
          <span className="icr-change-chip icr-change-chip--removed">
            Only on left
          </span>
        </div>
        <label className="icr-compare-toggle">
          <input
            type="checkbox"
            checked={!showUnchanged}
            onChange={(event) => setShowUnchanged(!event.target.checked)}
          />
          Show differences only
          <small>{reportedDifferenceCount} reported</small>
        </label>
      </div>

      <div className="icr-table-scroll">
        <table className="icr-component-compare">
          <thead>
            <tr>
              <th>Borrower / employment</th>
              <th>Income type</th>
              <th className="icr-num">Human (optional)</th>
              <th className="icr-num">Addy</th>
              <th className="icr-num">Harness</th>
              {hasHumanTruth && <th>Human → Addy</th>}
              {hasHumanTruth && <th>Human → Harness</th>}
              <th>Addy → Harness</th>
              <th>What happened</th>
            </tr>
            <tr className="icr-component-compare__subhead">
              <th colSpan={2} />
              <th className="icr-num">Avail / Qual</th>
              <th className="icr-num">Avail / Qual</th>
              <th className="icr-num">Avail / Qual</th>
              {hasHumanTruth && <th>Avail / Qual</th>}
              {hasHumanTruth && <th>Avail / Qual</th>}
              <th>Avail / Qual</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleComponents.map((row) => (
              <Fragment key={row.path}>
                <tr>
                  <td>
                    <strong>{row.borrower}</strong>
                    <small>{row.employment}</small>
                  </td>
                  <td>
                    {row.incomeType}
                    {row.status && <small>{row.status}</small>}
                  </td>
                  <td className="icr-num">
                    <MoneyPairCell
                      available={row.humanAvailable}
                      qualifying={row.human}
                    />
                  </td>
                  <td className="icr-num">
                    <MoneyPairCell
                      available={row.addyAvailable}
                      qualifying={row.addy}
                    />
                  </td>
                  <td className="icr-num">
                    <MoneyPairCell
                      available={row.harnessAvailable}
                      qualifying={row.harness}
                    />
                  </td>
                  {hasHumanTruth && (
                    <td>
                      <DualChangeCell leftKey="human" rightKey="addy" row={row} />
                    </td>
                  )}
                  {hasHumanTruth && (
                    <td>
                      <DualChangeCell
                        leftKey="human"
                        rightKey="harness"
                        row={row}
                      />
                    </td>
                  )}
                  <td>
                    <DualChangeCell leftKey="addy" rightKey="harness" row={row} />
                  </td>
                  <td className="icr-component-reason">
                    {componentReason(row)}
                  </td>
                </tr>
                <tr className="icr-component-audit-row">
                  <td colSpan={hasHumanTruth ? 9 : 7}>
                    <details>
                      <summary>Show calculation evidence</summary>
                      <div className="icr-source-audit-grid">
                        <SourceAudit
                          label="Addy Step 1"
                          available={row.addyAvailable}
                          qualifying={row.addy}
                          status={row.addyStatus}
                          detail={row.addyDetail}
                        />
                        <SourceAudit
                          label="Harness"
                          available={row.harnessAvailable}
                          qualifying={row.harness}
                          status={row.harnessStatus}
                          detail={row.harnessDetail}
                        />
                      </div>
                    </details>
                  </td>
                </tr>
              </Fragment>
            ))}
            {visibleComponents.length === 0 && (
              <tr>
                <td colSpan={hasHumanTruth ? 9 : 7} className="icr-muted">
                  {components.length
                    ? "No component differences."
                    : "No normalized component rows were returned for this evaluation."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="icr-difference-list">
        <h4>Reported differences</h4>
        {comparisonGroups.map((group) => (
          <section
            className="icr-comparison-group"
            key={group.key}
            aria-labelledby={`comparison-${group.key}`}
          >
            <div className="icr-comparison-group__header">
              <h5 id={`comparison-${group.key}`}>{group.label}</h5>
              <span>
                {group.differences.length}{" "}
                {group.differences.length === 1 ? "difference" : "differences"}
              </span>
            </div>
            {group.differences.length === 0 ? (
              <p className="icr-muted">No differences for this comparison.</p>
            ) : (
              group.differences.map((difference, index) => {
                const path = getDifferencePath(difference, index);
                const key = explanationKey(
                  group.leftSource,
                  group.rightSource,
                  path,
                );
                const explanation = explanations[key];

                return (
                  <article key={`${key}-${index}`} className="icr-difference">
                    <div>
                      <strong>
                        {getDifferenceSummary(
                          difference,
                          group.leftSource,
                          group.rightSource,
                        )}
                      </strong>
                      <code>{path}</code>
                    </div>
                    <button
                      className="icr-btn"
                      type="button"
                      disabled={
                        loadingExplanationKey === key || Boolean(explanation)
                      }
                      onClick={() => explain(group, difference, index)}
                    >
                      {loadingExplanationKey === key
                        ? "Explaining…"
                        : explanation
                          ? "Explanation cached"
                          : "Explain difference"}
                    </button>
                    {explanation && (
                      <p className="icr-difference__explanation">
                        {explanation}
                      </p>
                    )}
                  </article>
                );
              })
            )}
          </section>
        ))}
        {legacyExplanations.length > 0 && (
          <section className="icr-comparison-group">
            <div className="icr-comparison-group__header">
              <h5>Earlier cached explanations</h5>
              <span>Legacy records</span>
            </div>
            {legacyExplanations.map((record) => (
              <p className="icr-difference__explanation" key={record.key}>
                {record.text}
              </p>
            ))}
          </section>
        )}
      </div>

      <div className="icr-loan-trend">
        <div>
          <h4>Latest-10 trend</h4>
          <p>
            Addy and Harness amounts, with optional human truth when available.
          </p>
        </div>
        <TrendChart runs={recentRuns} goldenLoanId={goldenLoanId} />
      </div>
    </section>
  );
}
